import { addToCart, getCart, updateCartItem } from "@app/lib/cart"
import { CartItem } from "@app/types/cartItem"
import { Product } from "@app/types/product"
import React, { Dispatch, SetStateAction, useCallback, useMemo } from "react"
import { dispatchCartUpdate } from "@app/utils/cart"

interface CartItemProductProps {
  cartItem: CartItem;
  product: Product;
  zCodeCartItem?: CartItem;
  zCodeProduct?: Product;
  zCodeDisclaimer?: string;
  isLoading?: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const CartItemProduct = ({ cartItem, product, zCodeCartItem, zCodeProduct, zCodeDisclaimer, isLoading, setIsLoading }: CartItemProductProps) => {
  const isGiftCard = useMemo(() => product.tags.includes("gift-card"), [product.tags]);
  const isEssential = useMemo(() => product.tags.includes("essential"), [product.tags]);
  const isPreOrder = useMemo(() => product.tags.includes("pre-order"), [product.tags]);
  const color = useMemo(
    () => cartItem.options_with_values.find((o) => o.name === "Color")?.value,
    [cartItem.options_with_values],
  );
  const size = useMemo(
    () => {
      return cartItem?.properties?._nonZVariantSize ?
        cartItem?.properties?._nonZVariantSize :
        cartItem.options_with_values.find((o) => o.name === "Size")?.value
    },
    [cartItem.options_with_values],
  );
  const personalisation = useMemo(() => cartItem.properties?.personalisation || null, [cartItem.properties]);

  const price = useMemo(() => {
    const price = cartItem.price / 100;
    const originalPrice = cartItem.original_price / 100;
    return {
      price,
      originalPrice
    };
  }, [cartItem.price, cartItem.original_price]);

  const quantity = useMemo(
    () => cartItem.quantity + (zCodeCartItem ? zCodeCartItem.quantity : 0),
    [cartItem?.quantity, zCodeCartItem?.quantity]
  )

  const formattedDisclaimer = useMemo(
    () => {
      if (zCodeCartItem) {
        return zCodeDisclaimer
          ?.replace("[z-quantity]", zCodeCartItem?.quantity?.toString() || "")
          ?.replace("[z-size]", zCodeCartItem?.options_with_values?.find((o) => o.name === "Size")?.value || "")
      } else if (
        cartItem?.properties?._nonZVariantId &&
        cartItem?.properties?._nonZVariantSize &&
        cartItem.options_with_values.find((o) => o.name === "Size")?.value !== cartItem?.properties?._nonZVariantSize
      ) {
        return zCodeDisclaimer
          ?.replace("[z-quantity]", cartItem?.quantity?.toString() || "")
          ?.replace("[z-size]", cartItem?.options_with_values?.find((o) => o.name === "Size")?.value || "")
      }

      return null
    },
    [zCodeDisclaimer, zCodeCartItem, cartItem, size]
  )

  const handleQuantityAdd = useCallback(async () => {
    setIsLoading(true);

    let cart
    let added = false
    let targetCartItem = cartItem

    // If Z-code exists, check quantity available for it
    if (zCodeCartItem) {
      const zQuantityInCart = zCodeCartItem?.quantity
      const zQuantityTotal = zCodeProduct?.variants?.edges?.find(edge => edge?.node?.id?.endsWith(`/${zCodeCartItem?.variant_id}`))?.node?.quantityAvailable || 0

      // Set target item to Z-code item if quantity available
      if (zQuantityTotal > zQuantityInCart) {
        targetCartItem = zCodeCartItem
      }
    }

    // If Z-code item in cart without current product. The existence of _nonZVariantId confirms this condition
    if (cartItem.properties._nonZVariantId) {
      const quantityInCart = cartItem?.quantity
      const quantityTotal = product?.variants?.edges?.find(edge => edge?.node?.id?.endsWith(`/${cartItem?.variant_id}`))?.node?.quantityAvailable || 0

      // Insufficient Z-code stock to satisfy quantity change, add current product
      if (quantityInCart >= quantityTotal) {
        added = true
        await addToCart({
          items: [
            {
              id: cartItem.properties._nonZVariantId,
              quantity: 1,
              properties: {
                _zVariantId: cartItem.variant_id.toString()
              }
            }
          ]
        })
        cart = await getCart()
      }
    }

    // Update cart item if item not added via Z-code logic above
    if (!added) {
      cart = await updateCartItem({ id: targetCartItem.key, quantity: targetCartItem.quantity + 1 });
    }

    setIsLoading(false);
    dispatchCartUpdate(cart)
  }, [cartItem.key, cartItem.quantity]);

  const handleQuantitySubtract = useCallback(async () => {
    setIsLoading(true);

    const cart = await updateCartItem({ id: cartItem.key, quantity: cartItem.quantity - 1 });
    setIsLoading(false);
    dispatchCartUpdate(cart)
  }, [cartItem.key, cartItem.quantity]);

  const handleRemove = useCallback(async () => {
    setIsLoading(true);

    if (zCodeCartItem) {
      await updateCartItem({ id: zCodeCartItem.key, quantity: 0 });
    }
    const cart = await updateCartItem({ id: cartItem.key, quantity: 0 });

    setIsLoading(false);
    dispatchCartUpdate(cart)
  }, [cartItem.key]);

  return (
    <div className="grid grid-cols-[96px_1fr] gap-4 py-4">
      <div className="">
        <img className="object-cover object-center w-[96px] h-[96px] rounded-1 bg-brand-cotton" src={cartItem.image} />
      </div>
      <div className="space-y-2 overflow-hidden">
        {isEssential && (
          <div className="text-[0]">
            <badge-essential data-small />
          </div>
        )}
        {isPreOrder && (
          <div className="text-[0]">
            <badge-custom color="blue" text="Pre-order" />
          </div>
        )}

        <div className="space-y-1">
          <div className="text-base text-brand-bistre truncate">{product.title}</div>
          <div className="text-sm text-neutrals-secondaryGrey">
            {isGiftCard ? (
              <>
                <div>Type: {cartItem.properties?.type && cartItem.properties.type.charAt(0).toUpperCase() + cartItem.properties.type.slice(1)}</div>
                <div>Recipient Name: {cartItem.properties?.recipient_name}</div>
                <div>Recipient Email: {cartItem.properties?.recipient_email}</div>
                <div>Sender Name: {cartItem.properties?.sender_name}</div>
              </>
            ) : (
              <>
                <div>Colour: {color}</div>
                <div>Size: {size}</div>
                {formattedDisclaimer && (
                  <div className="size-buttons--message mb-3">{formattedDisclaimer}</div>
                )}
                {personalisation && <div>Personalisation: {personalisation}</div>}
              </>
            )}
            <div className="flex gap-2">
              <div>Quantity:</div>
              <div className="flex items-center justify-center">
                <button
                  className="button-text disabled:cursor-not-allowed stroke-neutrals-secondaryGrey"
                  dangerouslySetInnerHTML={{ __html: window.iconMinusXs }}
                  disabled={quantity < 2 || isLoading}
                  onClick={handleQuantitySubtract}
                />
                <div className="w-[16px] text-center">{quantity}</div>
                <button
                  className="button-text disabled:cursor-not-allowed stroke-neutrals-secondaryGrey"
                  dangerouslySetInnerHTML={{ __html: window.iconPlusXs }}
                  disabled={isLoading}
                  onClick={handleQuantityAdd}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 items-center justify-between">
          <div className="flex gap-1 text-sm">
            {price.originalPrice !== price.price && (
              <div className="text-neutrals-secondaryGrey line-through">${price.originalPrice.toFixed(2)}</div>
            )}
            <div className="font-medium text-brand-bistre">${price.price.toFixed(2)}</div>
          </div>
          <div>
            <button
              className="button-text disabled:cursor-not-allowed stroke-neutrals-secondaryGrey"
              dangerouslySetInnerHTML={{ __html: window.iconBinXs }}
              disabled={isLoading}
              onClick={handleRemove}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
